// WARNING: This file is for internal use only by MemberManager.server.ts and MemberManager.client.ts.
// Do NOT import or use this file directly elsewhere in your codebase.

import { User, SupabaseClient } from "@supabase/supabase-js";
import { TenantRow, MembershipRow, InvitationRow } from "@/lib/types";
import { Resend } from "resend";

/**
 * Extract the base domain from a host string
 * e.g., "tenant.sunstatehoa.com" -> "sunstatehoa.com"
 * e.g., "tenant.sunstatecoa.com" -> "sunstatecoa.com"
 */
function extractBaseDomain(host: string): string {
  if (!host) return "sunstatehoa.com"; // fallback
  
  // Handle localhost for development
  if (host.includes("localhost")) {
    return "sunstatehoa.com"; // default for development
  }
  
  const parts = host.split(".");
  if (parts.length >= 2) {
    // Return the last two parts (domain.com)
    return parts.slice(-2).join(".");
  }
  
  return "sunstatehoa.com"; // fallback
}

export interface UserInviteResult {
  success: boolean;
  addedToMemberships?: boolean;
  invitation?: boolean;
  error?: string;
}

export async function getTenantInfo(
  supabase: SupabaseClient,
  tenantId: string
): Promise<Pick<TenantRow, "id" | "name" | "slug"> | null> {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("id, name, slug")
    .eq("id", tenantId)
    .single();

  if (error || !tenant) return null;
  return tenant;
}

export async function getTenantInfoBySlug(
  supabase: SupabaseClient,
  slug: string
): Promise<Pick<TenantRow, "id" | "name" | "slug"> | null> {
  const { data: tenant, error } = await supabase
    .from("tenants")
    .select("id, name, slug")
    .eq("slug", slug)
    .single();
  if (error || !tenant) return null;
  return tenant;
}

export async function getAllTenants(
  supabase: SupabaseClient
): Promise<Pick<TenantRow, "id" | "name" | "slug">[]> {
  const { data, error } = await supabase
    .from("tenants")
    .select("id, name, slug")
    .order("name", { ascending: true });
  if (error) return [];
  return data || [];
}

export async function findUserByEmail(
  supabase: SupabaseClient,
  email: string
): Promise<string | null> {
  const { data: users, error } = await supabase.auth.admin.listUsers();
  if (error || !users?.users) return null;

  const user = users.users.find(
    (u) => u.email && u.email.toLowerCase() === email.toLowerCase().trim()
  );

  return user?.id || null;
}

export async function checkUserMembership(
  supabase: SupabaseClient,
  userId: string,
  tenantId: string
): Promise<boolean> {
  const { data: membership, error } = await supabase
    .from("memberships")
    .select("id")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .maybeSingle();

  return !error && !!membership;
}

export async function getUserMembershipRole(
  supabase: SupabaseClient,
  userId: string,
  tenantId: string
): Promise<"admin" | "member" | null> {
  const { data: membership } = await supabase
    .from("memberships")
    .select("has_admin_privileges")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .single();

  if (!membership) return null;
  return membership.has_admin_privileges ? "admin" : "member";
}

export async function getUserMembershipType(
  supabase: SupabaseClient,
  userId: string,
  tenantId: string
): Promise<
  | "owner"
  | "board-member"
  | "renter"
  | "property-manager"
  | "portal-admin"
  | null
> {
  const { data: membership } = await supabase
    .from("memberships")
    .select("member_type")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .single();

  if (!membership) return null;
  return membership.member_type;
}

export async function checkUserIsAdmin(
  supabase: SupabaseClient,
  userId: string,
  tenantId: string
): Promise<boolean> {
  const { data: membership } = await supabase
    .from("memberships")
    .select("has_admin_privileges")
    .eq("tenant_id", tenantId)
    .eq("user_id", userId)
    .single();

  return membership?.has_admin_privileges || false;
}

export async function addUserToMembership(
  supabase: SupabaseClient,
  userId: string,
  tenantId: string,
  memberType: "owner" | "board-member" | "renter" | "property-manager",
  hasAdminPrivileges: boolean
): Promise<boolean> {
  // Check if membership already exists
  const existingMembership = await checkUserMembership(
    supabase,
    userId,
    tenantId
  );
  if (existingMembership) {
    // Membership already exists, return true
    return true;
  }

  const { data, error } = await supabase.from("memberships").insert({
    tenant_id: tenantId,
    user_id: userId,
    member_type: memberType,
    has_admin_privileges: hasAdminPrivileges,
  });

  if (error) {
    console.error("Failed to add user to membership:", {
      error: error.message,
      details: error.details,
      hint: error.hint,
      userId,
      tenantId,
      memberType,
      hasAdminPrivileges,
    });
  }

  return !error;
}

export async function createUser(
  supabase: SupabaseClient,
  email: string
): Promise<string | null> {
  const { data: userData, error } = await supabase.auth.admin.createUser({
    email: email.toLowerCase().trim(),
    email_confirm: false,
  });

  if (error || !userData?.user?.id) return null;
  return userData.user.id;
}

export async function inviteUser(
  supabase: SupabaseClient,
  email: string,
  tenantName: string,
  redirectTo: string
): Promise<User> {
  const resend = new Resend(process.env.RESEND_API_KEY);
  const { data, error } = await supabase.auth.admin.generateLink({
    type: "magiclink",
    email: email,
    options: {
      data: { tenant: tenantName },
      redirectTo: redirectTo,
    },
  });

  if (error) {
    console.error("Failed to generate magic link:", error.message);
    throw error;
  }

  const actionLink = data?.properties?.action_link;
  if (!actionLink) {
    throw new Error("No action_link returned from Supabase");
  }

  // Send email using Resend
  const { error: sendError } = await resend.emails.send({
    from: "Sun State Administrator <admin@sunstatehoa.com>",
    to: email,
    subject: "Welcome to Sun State!",
    html: `
      <h2>You’ve been invited to join ${tenantName}</h2>
      <p>Welcome! Click the link below to log in and finish setting up your account.</p>
      <p><a href="${actionLink}"  style="display:inline-block;padding:10px 20px; background-color:#4f46e5;color:#fff;text-decoration:none;border-radius:5px;">Access Your Association Portal</a></p>
      <p>— The Sun State Team</p>
    `,
  });

  if (sendError) {
    console.error("Failed to send invite email via Resend:", sendError.message);
    throw sendError;
  }

  return data.user;
}

export async function createInvitationRecord(
  supabase: SupabaseClient,
  tenantId: string,
  userId: string,
  email: string,
  memberType: "owner" | "board-member" | "renter" | "property-manager",
  hasAdminPrivileges: boolean,
  invitedBy: string,
  message?: string
): Promise<boolean> {
  const { error } = await supabase.from("invitations").insert({
    tenant_id: tenantId,
    user_id: userId,
    email: email.toLowerCase().trim(),
    member_type: memberType,
    has_admin_privileges: hasAdminPrivileges,
    invited_by: invitedBy,
    message: message?.trim() || null,
    status: "pending",
  });

  return !error;
}

export async function getInvitationById(
  supabase: SupabaseClient,
  invitationId: string
): Promise<InvitationRow | null> {
  const { data: invitation, error } = await supabase
    .from("invitations")
    .select("*")
    .eq("id", invitationId)
    .single();
  if (error || !invitation) return null;
  return invitation;
}

// Deprecated: Do not use. Invitations should not be deleted for audit purposes.
export async function deleteInvitationById(
  supabase: SupabaseClient,
  invitationId: string
): Promise<boolean> {
  // No-op for audit trail
  return true;
}

export async function getMembershipsByTenantId(
  supabase: SupabaseClient,
  tenantId: string
): Promise<MembershipRow[]> {
  const { data, error } = await supabase
    .from("memberships")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });
  if (error) return [];
  return data || [];
}

export async function getMembershipsByUserId(
  supabase: SupabaseClient,
  userId: string
): Promise<MembershipRow[]> {
  const { data, error } = await supabase
    .from("memberships")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) return [];
  return data || [];
}

export async function getAllUsers(supabase: SupabaseClient): Promise<User[]> {
  const allUsers: User[] = [];
  let page = 1;
  const perPage = 1000; // Supabase default max per page
  
  while (true) {
    const { data, error } = await supabase.auth.admin.listUsers({
      page,
      perPage,
    });
    
    if (error) {
      console.error(`Error fetching users page ${page}:`, error);
      break;
    }
    
    if (!data?.users || data.users.length === 0) {
      break;
    }
    
    allUsers.push(...data.users);
    
    // If we got fewer users than perPage, we've reached the end
    if (data.users.length < perPage) {
      break;
    }
    
    page++;
  }
  
  return allUsers;
}

export async function getUsersByIds(
  supabase: SupabaseClient,
  userIds: string[]
): Promise<Map<string, User>> {
  const userMap = new Map<string, User>();
  
  // Fetch users individually (Supabase doesn't support batch getUserById)
  await Promise.all(
    userIds.map(async (userId) => {
      try {
        const { data, error } = await supabase.auth.admin.getUserById(userId);
        if (!error && data?.user) {
          userMap.set(userId, data.user);
        }
      } catch (err) {
        console.error(`Error fetching user ${userId}:`, err);
      }
    })
  );
  
  return userMap;
}

export async function getMembershipsWithUsersByTenantId(
  supabase: SupabaseClient,
  tenantId: string
): Promise<any[]> {
  // Step 1: Get all memberships for this tenant
  const { data: memberships, error: membershipsError } = await supabase
    .from("memberships")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false });

  if (membershipsError || !memberships) {
    console.error("Error fetching memberships:", membershipsError);
    return [];
  }

  if (memberships.length === 0) {
    return [];
  }

  // Step 2: Get all invitations for this tenant to check completion status
  const { data: invitations, error: invitationsError } = await supabase
    .from("invitations")
    .select("user_id, status")
    .eq("tenant_id", tenantId);

  // Create a map of user_id to their invitation status (completed = true means they've logged in)
  const userInvitationStatusMap = new Map<string, string>();
  if (invitations && !invitationsError) {
    invitations.forEach((inv: any) => {
      // Store the latest/most relevant status for each user
      const existing = userInvitationStatusMap.get(inv.user_id);
      // Priority: completed > pending > expired
      if (!existing || (inv.status === "completed") || (existing === "expired" && inv.status === "pending")) {
        userInvitationStatusMap.set(inv.user_id, inv.status);
      }
    });
  }

  // Filter memberships to only include users who have completed their invitation
  const activeMemberships = memberships.filter((membership: MembershipRow) => {
    const invitationStatus = userInvitationStatusMap.get(membership.user_id);
    // Only include if they have a completed invitation (i.e., they've logged in)
    // If no invitation record exists, include them (legacy users or direct additions)
    return !invitationStatus || invitationStatus === "completed";
  });

  if (activeMemberships.length === 0) {
    return [];
  }

  // Step 3: Get all auth users (with pagination)
  const allUsers = await getAllUsers(supabase);
  
  // Create a map for quick lookup
  const usersMap = new Map<string, User>();
  allUsers.forEach(user => usersMap.set(user.id, user));

  // Step 4: Combine memberships with user data
  const membershipsWithUsers = activeMemberships.map((membership: MembershipRow) => {
    const user = usersMap.get(membership.user_id);
    
    if (!user) {
      console.warn(`User not found in auth system for membership ${membership.id}, user_id: ${membership.user_id}`);
      // Return membership with placeholder user data
      return {
        ...membership,
        unit: membership.unit ?? undefined,
        first_name: membership.first_name ?? undefined,
        last_name: membership.last_name ?? undefined,
        users: {
          id: membership.user_id,
          email: `[User Not Found: ${membership.user_id.slice(0, 8)}...]`,
          created_at: membership.created_at
        }
      };
    }

    return {
      ...membership,
      unit: membership.unit ?? undefined,
      first_name: membership.first_name ?? undefined,
      last_name: membership.last_name ?? undefined,
      users: {
        id: user.id,
        email: user.email ?? "",
        created_at: user.created_at
      }
    };
  });

  return membershipsWithUsers;
}

export async function getPendingInvitationsByTenantId(
  supabase: SupabaseClient,
  tenantId: string
): Promise<InvitationRow[]> {
  const { data, error } = await supabase
    .from("invitations")
    .select("*")
    .eq("tenant_id", tenantId)
    .in("status", ["pending", "expired"])
    .order("invited_at", { ascending: false });
  if (error) return [];
  return data || [];
}

export async function checkInvitationByEmail(
  supabase: SupabaseClient,
  email: string,
  tenantId: string
): Promise<InvitationRow | null> {
  const { data, error } = await supabase
    .from("invitations")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("email", email.toLowerCase().trim())
    .eq("status", "pending")
    .single();
  if (error || !data) return null;
  return data;
}

export async function resendInvitation(
  supabase: SupabaseClient,
  email: string,
  tenantId: string,
  invitationId: string,
  role: string,
  host?: string
): Promise<boolean> {
  try {
    // Get tenant info for the email template
    const tenant = await getTenantInfo(supabase, tenantId);
    if (!tenant) {
      throw new Error("Tenant not found");
    }

    // Set redirectTo based on environment with dynamic domain
    const baseDomain = extractBaseDomain(host || "");
    const redirectTo =
      process.env.NODE_ENV === "development"
        ? `http://${tenant.slug}.${baseDomain}:3001/auth/callback`
        : `https://${tenant.slug}.${baseDomain}/auth/callback`;

    // Generate magic link using Supabase
    const { data, error } = await supabase.auth.admin.generateLink({
      type: "magiclink",
      email: email,
      options: {
        data: { tenant: tenant.name },
        redirectTo: redirectTo,
      },
    });

    if (error) {
      console.error("Failed to generate magic link:", error.message);
      throw error;
    }

    const actionLink = data?.properties?.action_link;
    if (!actionLink) {
      throw new Error("No action_link returned from Supabase");
    }

    // Send email using Resend
    const resend = new Resend(process.env.RESEND_API_KEY);
    const { error: sendError } = await resend.emails.send({
      from: "Sun State Administrator <admin@sunstatehoa.com>",
      to: email,
      subject: "Welcome to Sun State!",
      html: `
        <h2>You've been invited to join ${tenant.name}</h2>
        <p>Welcome! Click the link below to log in and finish setting up your account.</p>
        <p><a href="${actionLink}"  style="display:inline-block;padding:10px 20px; background-color:#4f46e5;color:#fff;text-decoration:none;border-radius:5px;">Access Your Association Portal</a></p>
        <p>— The Sun State Team</p>
      `,
    });

    if (sendError) {
      console.error(
        "Failed to send invite email via Resend:",
        sendError.message
      );
      throw sendError;
    }

    return true;
  } catch (error: unknown) {
    console.error("Resend invitation error:", error);
    return false;
  }
}

export async function updateInvitationTimestamp(
  supabase: SupabaseClient,
  invitationId: string
): Promise<boolean> {
  // When resending we want BOTH "Sent" and "Expires" to move forward.
  // We preserve the original TTL (expires_at - invited_at) so this matches
  // whatever default/trigger the DB uses for invitation expiry.
  const { data: invitation, error: fetchError } = await supabase
    .from("invitations")
    .select("invited_at, expires_at")
    .eq("id", invitationId)
    .single();

  if (fetchError || !invitation) return false;

  const invitedAtMs = new Date(invitation.invited_at).getTime();
  const expiresAtMs = new Date(invitation.expires_at).getTime();
  let ttlMs = expiresAtMs - invitedAtMs;

  // Fallback if dates are missing/invalid or TTL is non-positive.
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    ttlMs = 7 * 24 * 60 * 60 * 1000; // 7 days
  }

  const nowMs = Date.now();
  const nowIso = new Date(nowMs).toISOString();
  const newExpiresIso = new Date(nowMs + ttlMs).toISOString();

  const { error: updateError } = await supabase
    .from("invitations")
    .update({
      invited_at: nowIso,
      expires_at: newExpiresIso,
      // If an invite had been marked expired, resending should make it "pending" again.
      status: "pending",
      // If this was somehow previously completed, resending should re-open it.
      completed_at: null,
    })
    .eq("id", invitationId);

  return !updateError;
}

export async function updateMembershipRole(
  supabase: SupabaseClient,
  membershipId: string,
  role: "admin" | "member"
): Promise<boolean> {
  const { error } = await supabase
    .from("memberships")
    .update({ has_admin_privileges: role === "admin" })
    .eq("id", membershipId);
  return !error;
}

export async function updateMembershipMemberType(
  supabase: SupabaseClient,
  membershipId: string,
  memberType:
    | "owner"
    | "board-member"
    | "renter"
    | "property-manager"
    | "portal-admin"
): Promise<boolean> {
  const { error } = await supabase
    .from("memberships")
    .update({ member_type: memberType })
    .eq("id", membershipId);

  if (error) {
    console.error("Failed to update membership member type:", {
      error: error.message,
      details: error.details,
      hint: error.hint,
      membershipId,
      memberType,
    });
  }

  return !error;
}

export async function updateMembershipAdminPrivileges(
  supabase: SupabaseClient,
  membershipId: string,
  hasAdminPrivileges: boolean
): Promise<boolean> {
  const { error } = await supabase
    .from("memberships")
    .update({ has_admin_privileges: hasAdminPrivileges })
    .eq("id", membershipId);
  return !error;
}

export async function deleteMembershipById(
  supabase: SupabaseClient,
  membershipId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("memberships")
    .delete()
    .eq("id", membershipId);
  return !error;
}

export async function getMembershipById(
  supabase: SupabaseClient,
  membershipId: string
): Promise<MembershipRow | null> {
  const { data, error } = await supabase
    .from("memberships")
    .select("*")
    .eq("id", membershipId)
    .single();
  if (error || !data) return null;
  return data;
}

export async function deleteMembershipsByUserId(
  supabase: SupabaseClient,
  userId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("memberships")
    .delete()
    .eq("user_id", userId);
  return !error;
}

// Deprecated: Do not use. Invitations should not be deleted for audit purposes.
export async function deleteInvitationsByUserId(
  supabase: SupabaseClient,
  userId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("invitations")
    .delete()
    .eq("user_id", userId);
  return !error;
}

export async function markInvitationCompletedById(
  supabase: SupabaseClient,
  invitationId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("invitations")
    .update({ status: "completed", completed_at: new Date().toISOString() })
    .eq("id", invitationId);
  return !error;
}

export async function markInvitationsCompletedByUserId(
  supabase: SupabaseClient,
  userId: string,
  email?: string
): Promise<boolean> {
  let error = null;
  // Update by user_id
  const { error: userIdError } = await supabase
    .from("invitations")
    .update({ status: "completed", completed_at: new Date().toISOString() })
    .eq("user_id", userId)
    // A user may complete onboarding after an invite is marked expired.
    // Treat both pending/expired as completable.
    .in("status", ["pending", "expired", "waiting"]);
  if (userIdError) error = userIdError;
  // If email is provided, also update by email
  if (email) {
    const { error: emailError } = await supabase
      .from("invitations")
      .update({ status: "completed", completed_at: new Date().toISOString() })
      .eq("email", email.toLowerCase().trim())
      .in("status", ["pending", "expired", "waiting"]);
    if (emailError) error = emailError;
  }
  return !error;
}

export async function cancelInvitationById(
  supabase: SupabaseClient,
  invitationId: string
): Promise<boolean> {
  const { error } = await supabase
    .from("invitations")
    .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
    .eq("id", invitationId);
  return !error;
}

export async function getMembershipByEmail(
  supabase: SupabaseClient,
  email: string,
  tenantId: string
): Promise<MembershipRow | null> {
  // Find user by email
  const { data: users, error: userError } =
    await supabase.auth.admin.listUsers();
  if (userError || !users?.users) return null;
  const user = users.users.find(
    (u) => u.email && u.email.toLowerCase() === email.toLowerCase().trim()
  );
  if (!user?.id) return null;
  // Find membership by user id and tenant id
  const { data: membership, error: membershipError } = await supabase
    .from("memberships")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("user_id", user.id)
    .maybeSingle();
  if (membershipError || !membership) return null;
  return membership as MembershipRow;
}
